/**
 * Terrace bay-segmentation maths (issue #121, the #115 follow-on). A long
 * residential row mapped as ONE OSM way (e.g. way/763304999, ~105 m) renders
 * today as a single monolithic mass; this module decides whether such a
 * footprint is a terrace and, if so, splits it into per-dwelling bays so the
 * row reads as the ~19 dwellings it is on the ground.
 *
 * Pure, three.js-free, deterministic, unit-tested directly in Node — mirrors
 * the geometry-builder pattern in buildings.ts (footprintToLocal, ridgeAxis,
 * buildRoof) and roofLobes.ts (decomposeFootprint). Nothing here touches
 * raypath/ or any LOS result: bay segmentation is scene furniture only (hard
 * constraint #1, guarded by buildings.guard.test.ts). No elevation is invented
 * here — a bay's height always derives from the whole footprint's own height
 * (hard constraints #6/#7); see steppedEaves.
 *
 * The workhorse is the spike's "candidate algorithm C": clip the footprint
 * along its MINIMUM-AREA oriented bounding box's long axis into round(long/5.5)
 * parallel slabs (5.5 m ≈ a typical UK terraced frontage), each an exact
 * half-plane (Sutherland–Hodgman) clip so the rear wings and steps of a real
 * outline are preserved rather than boxed over. Targeting is use-aware
 * (isTerraceCandidate) so agricultural/industrial long sheds are left as single
 * masses.
 */
import type { LocalXZ } from '../core/coordinates';
import { polygonArea } from './roofLobes';
import { isValidHexColor } from './facadeTexture';

/** Typical UK terraced-house frontage (m); the bay width the row is divided by. */
export const BAY_WIDTH_M = 5.5;

/** Geometric terrace predicate thresholds (issue #121 / #115 spike). A footprint
 * with no `building=terrace` tag must clear ALL of these to be segmented. */
export const TERRACE_LONG_AXIS_MIN_M = 30;
export const TERRACE_ASPECT_MIN = 2.5;
export const TERRACE_FILL_MIN = 0.6;

/** Amplitude (m) of the deterministic per-bay eaves step that gives a row its
 * stepped-terrace silhouette. Applied around the footprint's own base height —
 * never re-invents a height (hard constraint #7). */
export const TERRACE_EAVES_STEP_M = 0.8;

/** A small positive floor so a stepped bay can never collapse to a non-positive
 * wall height on a very short base (would emit degenerate geometry). */
const MIN_BAY_EAVES_M = 2.0;

/** OSM `building=` values that must NEVER be dressed as dwellings even when long
 * and thin (barns, sheds, industrial units, silos, garages). The `terrace` tag
 * bypasses this set; the geometric path is gated by it. Mirrors the spike's
 * use-aware exclusion and buildings.ts's NON_DOMESTIC_OSM_BUILDING intent. */
export const TERRACE_EXCLUDED_OSM_BUILDING: ReadonlySet<string> = new Set([
  'barn',
  'industrial',
  'farm_auxiliary',
  'silo',
  'stable',
  'shed',
  'garage',
  'garages',
]);

const EPS = 1e-9;

/** Deterministic fractional hash in [0, 1) seeded by an integer (same idiom as
 * buildings.ts / turbines.ts). Kept local so this module has no dependency on
 * buildings.ts (which imports this one — avoids an import cycle). */
export function hash01(i: number): number {
  const x = Math.sin(i * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/**
 * Minimum-area oriented bounding box of a footprint ring (open ring, local
 * x/z). Unlike ridgeAxis/orientedBbox (which align to the single longest edge),
 * this rotates over every convex-hull edge (rotating calipers) and keeps the
 * box of least area — the honest "how long is this building" the spike measures,
 * stricter than the longest-edge box. Returns null for a degenerate ring whose
 * hull has fewer than 3 distinct points.
 */
export interface MinAreaObb {
  /** the longer side length (m) */
  long: number;
  /** the shorter side length (m) */
  short: number;
  /** unit direction along the long side */
  longDir: { x: number; z: number };
  /** unit direction along the short side (longDir rotated +90 degrees) */
  shortDir: { x: number; z: number };
  /** box centre, local x/z */
  centre: LocalXZ;
  /** min projection of the ring onto longDir (the along-axis lower bound) */
  alongMin: number;
  /** max projection of the ring onto longDir (the along-axis upper bound) */
  alongMax: number;
}

/** Andrew's monotone-chain convex hull (counter-clockwise), deduped. */
function convexHull(points: LocalXZ[]): LocalXZ[] {
  const pts = points
    .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.z))
    .map((p) => ({ x: p.x, z: p.z }))
    .sort((a, b) => (a.x === b.x ? a.z - b.z : a.x - b.x));
  const uniq: LocalXZ[] = [];
  for (const p of pts) {
    const last = uniq[uniq.length - 1];
    if (!last || Math.abs(last.x - p.x) > 1e-12 || Math.abs(last.z - p.z) > 1e-12) uniq.push(p);
  }
  if (uniq.length < 3) return uniq;
  const cross = (o: LocalXZ, a: LocalXZ, b: LocalXZ) => (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x);
  const lower: LocalXZ[] = [];
  for (const p of uniq) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: LocalXZ[] = [];
  for (let i = uniq.length - 1; i >= 0; i--) {
    const p = uniq[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

export function minAreaObb(ring: LocalXZ[]): MinAreaObb | null {
  if (ring.length < 3) return null;
  const hull = convexHull(ring);
  if (hull.length < 3) return null;

  let best: {
    area: number;
    dir: { x: number; z: number };
    perp: { x: number; z: number };
    minU: number;
    maxU: number;
    minV: number;
    maxV: number;
  } | null = null;

  const n = hull.length;
  for (let i = 0; i < n; i++) {
    const a = hull[i];
    const b = hull[(i + 1) % n];
    const ex = b.x - a.x;
    const ez = b.z - a.z;
    const len = Math.hypot(ex, ez);
    if (len < 1e-9) continue;
    const dir = { x: ex / len, z: ez / len };
    const perp = { x: -dir.z, z: dir.x };
    let minU = Infinity;
    let maxU = -Infinity;
    let minV = Infinity;
    let maxV = -Infinity;
    for (const p of hull) {
      const u = p.x * dir.x + p.z * dir.z;
      const v = p.x * perp.x + p.z * perp.z;
      if (u < minU) minU = u;
      if (u > maxU) maxU = u;
      if (v < minV) minV = v;
      if (v > maxV) maxV = v;
    }
    const area = (maxU - minU) * (maxV - minV);
    if (best === null || area < best.area - 1e-12) {
      best = { area, dir, perp, minU, maxU, minV, maxV };
    }
  }
  if (best === null) return null;

  const wU = best.maxU - best.minU;
  const wV = best.maxV - best.minV;
  // Long axis is whichever of (dir, perp) spans the greater extent.
  const longIsU = wU >= wV;
  const longDir = longIsU ? best.dir : best.perp;
  const shortDir = longIsU ? best.perp : best.dir;
  const long = longIsU ? wU : wV;
  const short = longIsU ? wV : wU;
  const alongMin = longIsU ? best.minU : best.minV;
  const alongMax = longIsU ? best.maxU : best.maxV;
  const acrossMid = longIsU ? (best.minV + best.maxV) / 2 : (best.minU + best.maxU) / 2;
  const alongMid = (alongMin + alongMax) / 2;
  const centre = {
    x: longDir.x * alongMid + shortDir.x * acrossMid,
    z: longDir.z * alongMid + shortDir.z * acrossMid,
  };
  return { long, short, longDir, shortDir, centre, alongMin, alongMax };
}

/** Long axis, short axis, aspect (long/short) and rectangular fill (area / OBB
 * area) of a footprint — the four numbers the terrace predicate reads. Null for
 * a degenerate ring. */
export function terraceMetrics(ring: LocalXZ[]): { long: number; short: number; aspect: number; fill: number } | null {
  const obb = minAreaObb(ring);
  if (!obb || obb.short <= EPS) return null;
  const aspect = obb.long / obb.short;
  const boxArea = obb.long * obb.short;
  const fill = boxArea > EPS ? polygonArea(ring) / boxArea : 0;
  return { long: obb.long, short: obb.short, aspect, fill };
}

/**
 * Whether a footprint should be bay-segmented (issue #121 use-aware target):
 *   - `osm_building == "terrace"` -> yes, on the tag alone (bypasses the fill
 *     floor, so the low-fill exemplar way/763304999 at fill 0.47 is included);
 *   - otherwise the geometric path: long axis >= 30 m AND aspect >= 2.5 AND
 *     rectangular fill >= 0.6 AND `osm_building` NOT in the excluded set. The
 *     fill floor drops bent/L/dogleg ranges (the spike no-go, e.g.
 *     way/361533013 at fill 0.53) and the excluded set keeps barns/sheds out.
 * A missing `osm_building` is never a blocker (hard constraint #7): it simply
 * takes the geometric path and, since it is not in the excluded set, is
 * admitted iff the geometry qualifies. Never throws.
 */
export function isTerraceCandidate(osmBuilding: string | undefined, ring: LocalXZ[]): boolean {
  if (osmBuilding === 'terrace') return true;
  if (osmBuilding != null && TERRACE_EXCLUDED_OSM_BUILDING.has(osmBuilding)) return false;
  const m = terraceMetrics(ring);
  if (!m) return false;
  return m.long >= TERRACE_LONG_AXIS_MIN_M && m.aspect >= TERRACE_ASPECT_MIN && m.fill >= TERRACE_FILL_MIN;
}

/** Number of per-dwelling bays a footprint splits into: round(long / 5.5),
 * floored at 1. A degenerate ring yields 1 (single mass). */
export function bayCount(ring: LocalXZ[]): number {
  const obb = minAreaObb(ring);
  if (!obb || !(obb.long > 0)) return 1;
  return Math.max(1, Math.round(obb.long / BAY_WIDTH_M));
}

/** Sutherland–Hodgman clip of a simple polygon against the half-plane where the
 * signed distance nx*(x-ox)+nz*(z-oz) is <= 0 (keeps that side). Concave inputs
 * clip correctly (the region is convex), so a slab intersection preserves area
 * exactly. Mirrors roofLobes.clipHalfPlane (kept local to stay self-contained). */
function clipHalfPlane(poly: LocalXZ[], ox: number, oz: number, nx: number, nz: number): LocalXZ[] {
  const out: LocalXZ[] = [];
  const n = poly.length;
  const side = (p: LocalXZ) => nx * (p.x - ox) + nz * (p.z - oz);
  for (let i = 0; i < n; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % n];
    const sa = side(a);
    const sb = side(b);
    if (sa <= 1e-12) out.push(a);
    if ((sa < -1e-12 && sb > 1e-12) || (sa > 1e-12 && sb < -1e-12)) {
      const t = sa / (sa - sb);
      out.push({ x: a.x + t * (b.x - a.x), z: a.z + t * (b.z - a.z) });
    }
  }
  const dedup: LocalXZ[] = [];
  for (let i = 0; i < out.length; i++) {
    const p = out[i];
    const q = out[(i + 1) % out.length];
    if (Math.hypot(p.x - q.x, p.z - q.z) > 1e-7) dedup.push(p);
  }
  return dedup;
}

/**
 * Splits a footprint ring into per-dwelling bays by clipping it into equal-width
 * parallel slabs along the minimum-area OBB long axis. Returns:
 *   - `[ring]` unchanged when bayCount <= 1 (short/near-square footprint, or a
 *     degenerate ring) — the caller's single-mass path then reproduces today's
 *     geometry byte-for-byte;
 *   - N bay sub-polygons otherwise, each an exact half-plane-clipped piece of
 *     the real outline (rear wings preserved). The slabs partition the along
 *     axis, so the bay areas sum to the footprint area even for a concave
 *     outline whose OBB overhangs a wing.
 * Never throws: any failure falls back to `[ring]` (hard constraint #7).
 */
export function segmentFootprint(ring: LocalXZ[]): LocalXZ[][] {
  if (ring.length < 3) return [ring];
  let obb: MinAreaObb | null;
  try {
    obb = minAreaObb(ring);
  } catch {
    return [ring];
  }
  if (!obb || !(obb.long > 0)) return [ring];
  const n = Math.max(1, Math.round(obb.long / BAY_WIDTH_M));
  if (n <= 1) return [ring];

  const { longDir, alongMin, alongMax } = obb;
  const width = (alongMax - alongMin) / n;
  if (!(width > 0)) return [ring];

  const bays: LocalXZ[][] = [];
  for (let k = 0; k < n; k++) {
    const lo = alongMin + k * width;
    const hi = k === n - 1 ? alongMax : alongMin + (k + 1) * width;
    // Keep along(p) >= lo: normal = -longDir, origin at along=lo.
    // Keep along(p) <= hi: normal = +longDir, origin at along=hi.
    let bay = clipHalfPlane(ring, lo * longDir.x, lo * longDir.z, -longDir.x, -longDir.z);
    bay = clipHalfPlane(bay, hi * longDir.x, hi * longDir.z, longDir.x, longDir.z);
    if (bay.length >= 3 && polygonArea(bay) > 1e-6) bays.push(bay);
  }
  return bays.length > 0 ? bays : [ring];
}

/**
 * Per-bay eaves height for the stepped-terrace silhouette. Takes the footprint's
 * OWN base eaves (from buildingHeight — measured, OSM, or nominal) and applies a
 * bounded, deterministic step in [-TERRACE_EAVES_STEP_M, +TERRACE_EAVES_STEP_M].
 * Never invents a height from scratch and never returns a non-positive wall
 * height (floored at MIN_BAY_EAVES_M). Deterministic per (base, bayIndex, seed).
 */
export function steppedEaves(baseEaves: number, bayIndex: number, seed: number): number {
  const step = (hash01(seed * 1009 + bayIndex * 31 + 1) - 0.5) * 2 * TERRACE_EAVES_STEP_M;
  return Math.max(MIN_BAY_EAVES_M, baseEaves + step);
}

/** One bay's resolved dwelling appearance (issue #121 reconciliation). Fields
 * are per-field nullable: `null` means "no observation, fall back for THIS field
 * only" (the caller uses the hash-derived per-bay value). */
export interface BayDwelling {
  /** index into the input dwellings[] array this bay was mapped from */
  dwellingIndex: number;
  /** observed door colour (validated hex) or null (fall back to hash door) */
  doorColor: string | null;
  /** observed window count (finite, >= 0) or null (fall back to default/hash) */
  windowCount: number | null;
}

interface DwellingCandidate {
  di: number;
  a: number;
  b: number;
  doorColor: string | null;
  windowCount: number | null;
}

function validExtent(raw: unknown): [number, number] | null {
  if (!Array.isArray(raw) || raw.length < 2) return null;
  const a = raw[0];
  const b = raw[1];
  if (typeof a !== 'number' || typeof b !== 'number' || !Number.isFinite(a) || !Number.isFinite(b)) return null;
  const lo = Math.max(0, Math.min(1, a));
  const hi = Math.max(0, Math.min(1, b));
  if (hi <= lo + 1e-9) return null; // zero-width or reversed extent -> invalid (never swap)
  return [lo, hi];
}

function validWindowCount(raw: unknown): number | null {
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) return null;
  return Math.floor(raw);
}

/**
 * Maps a classifier `dwellings[]` array onto the N geometry bays (roadmap §2c).
 * Each observed dwelling claims exactly ONE bay — the unoccupied bay it overlaps
 * most (tie-break: bay centre nearest the dwelling's extent centre, then lower
 * index). So N observed units decorate exactly N bays (never stretched across
 * more bays than were observed), the remaining bays return `null` (the caller
 * uses hash-derived variety), and no observed door is dropped while a bay is
 * free. When there are MORE observed dwellings than bays, bays are the limiting
 * resource: the first dwellings (in overlap-greedy order) claim all bays and the
 * surplus is unplaced — there is simply no bay to show them, not an arbitrary
 * drop. Deterministic; never throws on malformed input (returns all-null).
 */
export function reconcileDwellings(nBays: number, dwellings: unknown): (BayDwelling | null)[] {
  const out: (BayDwelling | null)[] = new Array(Math.max(0, nBays)).fill(null);
  if (nBays <= 0 || !Array.isArray(dwellings) || dwellings.length === 0) return out;

  const candidates: DwellingCandidate[] = [];
  dwellings.forEach((d, di) => {
    if (d == null || typeof d !== 'object') return;
    const rec = d as { bay_extent?: unknown; door_color?: unknown; window_group?: unknown };
    const ext = validExtent(rec.bay_extent);
    if (!ext) return;
    const doorColor = isValidHexColor(rec.door_color) ? (rec.door_color as string) : null;
    const wg = rec.window_group;
    const windowCount =
      wg != null && typeof wg === 'object' ? validWindowCount((wg as { count?: unknown }).count) : null;
    candidates.push({ di, a: ext[0], b: ext[1], doorColor, windowCount });
  });

  const occupied = new Array(nBays).fill(false);
  for (const c of candidates) {
    const extCentre = (c.a + c.b) / 2;
    let bestBay = -1;
    let bestOverlap = 0;
    let bestDist = Infinity;
    for (let k = 0; k < nBays; k++) {
      if (occupied[k]) continue;
      const lo = k / nBays;
      const hi = (k + 1) / nBays;
      const overlap = Math.min(hi, c.b) - Math.max(lo, c.a);
      if (overlap <= EPS) continue;
      const dist = Math.abs((lo + hi) / 2 - extCentre);
      if (overlap > bestOverlap + EPS || (Math.abs(overlap - bestOverlap) <= EPS && dist < bestDist - EPS)) {
        bestBay = k;
        bestOverlap = overlap;
        bestDist = dist;
      }
    }
    if (bestBay >= 0) {
      occupied[bestBay] = true;
      out[bestBay] = { dwellingIndex: c.di, doorColor: c.doorColor, windowCount: c.windowCount };
    }
  }
  return out;
}
